import type { CSSProperties } from 'react';
import { CUSTOM_CHART_SIZES, type CustomChartSize, type CustomChartType } from '@/entities/dashboards';
import { DASHBOARD_BLOCK_MAX_HEIGHT, cn } from '@/shared/lib';
import { dashboardGridHeight } from '../utils';

/**
 * Классы сетки дашборда: одна колонка на узком экране, 60 долей — на широком.
 *
 * На узком экране блоки идут один под другим в порядке разметки: по строкам, в строке — слева
 * направо, в колонке — сверху вниз. На широком у каждого блока своё место ({@link chartGridAreaStyle}),
 * а дорожки строк задаёт шаблон ({@link createChartGridTracks}).
 *
 * Зазор между диаграммами задают не промежутки сетки, а отступы самих ячеек ({@link
 * CHART_CELL_GUTTER_CLASS}) — по обеим сторонам. По ширине: 59 промежутков между 60 долями требуют
 * ширины больше той, что остаётся странице на невеликом экране, и сетка вылезала бы за неё вместе с
 * правыми диаграммами; отступ внутри ячейки долю ширины не занимает, поэтому сетка укладывается в
 * любую ширину. По высоте зазор задан тем же отступом: он один и у диаграмм, и у блоков, что встают
 * в сетку между строками, — места под новую диаграмму и развёрнутых свойств.
 *
 * Отступ ячеек сетка возвращает себе полями наружу — иначе крайние диаграммы отступали бы от краёв
 * страницы, а соседние с ними блоки — нет.
 */
export const CHART_GRID_CLASS = 'grid grid-cols-1 -my-2 lg:-mx-2 lg:grid-cols-60 lg:grid-rows-(--chart-grid-rows)';

/**
 * Отступ ячейки сетки: половина зазора с каждой стороны — вместе с соседкой выходит целый.
 *
 * По ширине это отступ внутри ячейки, по высоте — поле снаружи: высота ячейки задана высотой
 * диаграммы, и внутренний отступ отнял бы её у самой диаграммы.
 */
export const CHART_CELL_GUTTER_CLASS = 'my-2 lg:px-2';

/** Ширина сетки дашборда в колонках раскладки. */
const CHART_GRID_COLUMNS = 60;

/**
 * Колонки раскладки, которые занимает диаграмма каждой доли ширины каталога. Сетка из 60 колонок
 * делится нацело на все доли каталога («k/n» при n от 2 до 5), поэтому диаграммы встают без остатка,
 * а края диаграмм соседних строк из тех же долей совпадают. На узком экране сетка одноколоночная —
 * доля ширины там ничего не решает.
 */
export const CHART_SIZE_COLUMNS: Record<CustomChartSize, number> = {
  '1/5': 12,
  '1/4': 15,
  '1/3': 20,
  '2/5': 24,
  '1/2': 30,
  '3/5': 36,
  '2/3': 40,
  '3/4': 45,
  '4/5': 48,
  full: 60,
};

/** Диаграмма глазами раскладки: доля ширины, признак начала строки и место под соседкой. */
export type ChartGridPlacement = {
  size: CustomChartSize;
  /** Диаграмма начинает собой строку — предыдущая строка обрывается на соседке слева */
  startsRow?: boolean;
  /** Диаграмма встаёт под предыдущей, в её колонку строки, и ширины строки не занимает */
  stacksBelow?: boolean;
};

/** Колонка строки сетки: диаграммы одна под другой, общей шириной. */
export type ChartGridColumn = {
  /** Позиции диаграмм колонки в составе дашборда — сверху вниз */
  chartIndexes: number[];
  /** Доля ширины колонки — доля её верхней диаграммы */
  size: CustomChartSize;
};

/** Строка сетки дашборда: какие диаграммы и колонки в неё встали и сколько ширины в ней осталось. */
export type ChartGridRow = {
  /** Позиции диаграмм строки в составе дашборда */
  chartIndexes: number[];
  /** Колонки строки слева направо; одиночная диаграмма — колонка из одной */
  columns: ChartGridColumn[];
  /** Незанятый конец строки в колонках раскладки */
  freeColumns: number;
};

/**
 * Раскладывает состав по строкам так же, как его раскладывает сетка: диаграммы идут подряд, не
 * поместившаяся в остаток строки переходит на следующую, а помеченная началом строки открывает
 * новую сама. Помеченная местом под соседкой встаёт под предыдущей диаграммой, в её колонку:
 * ширину колонки задаёт верхняя диаграмма, поэтому ширины строки такая диаграмма не занимает.
 */
export const chartGridRows = (charts: ChartGridPlacement[]): ChartGridRow[] => {
  const rows: ChartGridRow[] = [];

  charts.forEach((chart, index) => {
    const currentRow = rows.at(-1);
    const currentColumn = currentRow?.columns.at(-1);
    if (chart.stacksBelow && currentRow && currentColumn) {
      currentRow.chartIndexes.push(index);
      currentColumn.chartIndexes.push(index);
      return;
    }
    const columns = CHART_SIZE_COLUMNS[chart.size];
    const column: ChartGridColumn = { chartIndexes: [index], size: chart.size };
    if (!currentRow || chart.startsRow || columns > currentRow.freeColumns) {
      rows.push({ chartIndexes: [index], columns: [column], freeColumns: CHART_GRID_COLUMNS - columns });
      return;
    }
    currentRow.chartIndexes.push(index);
    currentRow.columns.push(column);
    currentRow.freeColumns -= columns;
  });

  return rows;
};

/** Место блока на широкой сетке: колонки раскладки и дорожки строк, отсчёт с 1. */
export type ChartGridArea = {
  /** Первая колонка раскладки */
  column: number;
  /** Сколько колонок раскладки занимает блок */
  columns: number;
  /** Первая дорожка строк */
  row: number;
  /** Сколько дорожек строк занимает блок */
  rows: number;
};

/** Место диаграммы строки на широкой сетке. */
export type ChartGridPlace = {
  /** Позиция диаграммы в составе */
  chartIndex: number;
  /** Колонка строки, в которой стоит диаграмма */
  column: ChartGridColumn;
  area: ChartGridArea;
};

/** Места строки диаграмм на широкой сетке. */
export type ChartGridRowArea = {
  /** Места диаграмм строки в порядке состава: колонка за колонкой, в колонке — сверху вниз */
  places: ChartGridPlace[];
  /** Незанятый конец строки на всю её высоту; у занятой целиком строки он нулевой ширины */
  freeArea: ChartGridArea;
};

/**
 * Дорожка места в колонке строки, кроме нижнего: не выше самой высокой из диаграмм, стоящих на нём
 * в колонках строки. Высоту, которой не хватает диаграмме, занявшей несколько мест, получает не она,
 * а нижнее место строки.
 */
const CHART_SLOT_TRACK = 'minmax(min-content, 0px)';

/**
 * Дорожка нижнего места строки и блока между строками: по содержимому, в том числе по остатку высоты
 * диаграмм, занявших несколько мест.
 */
const CHART_FILL_TRACK = 'auto';

/**
 * Дорожки строк широкой сетки, набираемые сверху вниз: строки диаграмм и блоки между ними — свойства
 * диаграммы, место под новую строку.
 *
 * У строки диаграмм по дорожке на каждое место колонки сверху вниз: i-е диаграммы всех колонок строки
 * стоят на i-й дорожке, поэтому начинаются на одной высоте, а граница между ними идёт одной линией.
 * Высота места — по самой высокой из диаграмм на нём, более низкая дотягивается до неё. Нижняя
 * диаграмма колонки занимает своё место и все места ниже — до низа строки, — а одиночная диаграмма
 * занимает строку целиком; высоту, которой ей не хватает, добирает нижнее место строки, а не верхние.
 * Дотягивается и диаграмма с заданной высотой ({@link CHART_CELL_CLASS}), поэтому нижние края всех
 * диаграмм строки совпадают.
 */
export const createChartGridTracks = () => {
  const tracks: string[] = [];

  return {
    /** Ставит строку диаграмм следующими дорожками. */
    addRow: (row: ChartGridRow): ChartGridRowArea => {
      const first = tracks.length + 1;
      const slotCount = Math.max(...row.columns.map((column) => column.chartIndexes.length));
      const places: ChartGridPlace[] = [];
      let start = 1;
      row.columns.forEach((column) => {
        const columns = CHART_SIZE_COLUMNS[column.size];
        column.chartIndexes.forEach((chartIndex, slot) => {
          const isBottom = slot === column.chartIndexes.length - 1;
          const rows = isBottom ? slotCount - slot : 1;
          places.push({ chartIndex, column, area: { column: start, columns, row: first + slot, rows } });
        });
        start += columns;
      });
      for (let slot = 1; slot < slotCount; slot += 1) tracks.push(CHART_SLOT_TRACK);
      tracks.push(CHART_FILL_TRACK);
      return {
        places,
        freeArea: { column: start, columns: CHART_GRID_COLUMNS - start + 1, row: first, rows: slotCount },
      };
    },
    /** Ставит блок между строками во всю ширину сетки отдельной дорожкой; возвращает его место. */
    addBlockRow: (): ChartGridArea => {
      tracks.push(CHART_FILL_TRACK);
      return { column: 1, columns: CHART_GRID_COLUMNS, row: tracks.length, rows: 1 };
    },
    /** Стиль сетки с набранными дорожками — только для широкого экрана ({@link CHART_GRID_CLASS}). */
    gridStyle: (): CSSProperties => ({ ['--chart-grid-rows']: tracks.join(' ') }) as CSSProperties,
  };
};

/** Классы блока сетки, стоящего на своём месте: на широком экране — {@link chartGridAreaStyle}. */
export const CHART_GRID_AREA_CLASS = 'lg:col-(--chart-grid-column) lg:row-(--chart-grid-row)';

/** Место блока на широкой сетке стилем; на узком экране блоки идут по порядку разметки. */
export const chartGridAreaStyle = (area: ChartGridArea): CSSProperties =>
  ({
    ['--chart-grid-column']: `${area.column} / span ${area.columns}`,
    ['--chart-grid-row']: `${area.row} / span ${area.rows}`,
  }) as CSSProperties;

/** Колонка строки, в которой стоит диаграмма с позицией `chartIndex`; нет такой — `undefined`. */
export const findChartGridColumn = (rows: ChartGridRow[], chartIndex: number): ChartGridColumn | undefined =>
  rows.flatMap((row) => row.columns).find((column) => column.chartIndexes.includes(chartIndex));

/** Наименьшая доля ширины каталога в колонках раскладки — уже неё диаграмма не бывает. */
const MIN_CHART_COLUMNS = Math.min(...CUSTOM_CHART_SIZES.map((size) => CHART_SIZE_COLUMNS[size]));

/**
 * В незанятый конец строки помещается ещё одна диаграмма. По этому и показывается место под
 * новую: остаток уже наименьшей доли заводить в него нечем.
 */
export const hasRoomForChart = (freeColumns: number): boolean => freeColumns >= MIN_CHART_COLUMNS;

/** Зазор между ячейками сетки в пикселях — тот же, что складывается из {@link CHART_CELL_GUTTER_CLASS}. */
const CHART_GRID_GAP = 16;

/** Доля ширины сетки, которую занимает диаграмма этого размера каталога. */
export const chartSizeRatio = (size: CustomChartSize): number => CHART_SIZE_COLUMNS[size] / CHART_GRID_COLUMNS;

/**
 * Ближайшая к растянутой ширине доля каталога: граница диаграммы встаёт на ближайшую линию
 * допустимой доли, промежуточных значений диаграмма не принимает.
 *
 * Доля считается с поправкой на зазор: карточка стоит внутри отступов своей ячейки и уже её на
 * целый зазор, и без поправки растянутая ровно до половины диаграмма встаёт на 1/3.
 */
export const nearestChartSize = (widthPx: number, gridWidthPx: number): CustomChartSize => {
  if (gridWidthPx <= 0) return 'full';
  const ratio = (widthPx + CHART_GRID_GAP) / gridWidthPx;
  return CUSTOM_CHART_SIZES.reduce((nearest, size) =>
    Math.abs(chartSizeRatio(size) - ratio) < Math.abs(chartSizeRatio(nearest) - ratio) ? size : nearest,
  );
};

/** Наименьшая высота диаграммы: ниже неё не помещается даже заголовок с одной строкой данных. */
export const MIN_CHART_HEIGHT = 160;

/** Та же наименьшая высота стилем — для блоков сетки, которым её задаёт не растягивание. */
export const MIN_CHART_HEIGHT_STYLE: CSSProperties = { minHeight: MIN_CHART_HEIGHT };

/**
 * Типы на DataGrid: у них своя прокрутка строк и своя наименьшая таблица ({@link chartTableStyle}),
 * поэтому стягиваются они только до {@link MIN_CHART_HEIGHT}.
 */
const DATA_GRID_CHART_TYPES: ReadonlySet<CustomChartType> = new Set(['table', 'grouped-table', 'heatmap']);

/**
 * Растягиванием диаграмму этого типа можно стянуть до высоты по содержимому, если та меньше
 * {@link MIN_CHART_HEIGHT}: показатель, получивший при заведении высоту по содержимому, сжимается
 * обратно до неё, а не застревает на общей наименьшей высоте.
 */
export const isChartShrinkingToContent = (chartType: CustomChartType): boolean => !DATA_GRID_CHART_TYPES.has(chartType);

/**
 * Заданная растягиванием высота — в границах от наименьшей до потолка блоков дашборда.
 *
 * `contentHeightPx` — высота диаграммы по содержимому: наименьшая высота не больше неё, поэтому
 * диаграмму можно стянуть до той высоты, что она получает без заданной. Не задана — наименьшая
 * высота общая, {@link MIN_CHART_HEIGHT}.
 */
export const clampChartHeight = (heightPx: number, contentHeightPx?: number): number => {
  const minHeight =
    contentHeightPx === undefined ? MIN_CHART_HEIGHT : Math.min(MIN_CHART_HEIGHT, Math.ceil(contentHeightPx));
  return Math.min(Math.max(Math.round(heightPx), minHeight), DASHBOARD_BLOCK_MAX_HEIGHT);
};

/**
 * Ячейка не ниже своего содержимого, когда в нём место таблицы с легендой под ней (признак
 * `data-keeps-table` у `ChartCardShell`). Легенда видна целиком, и у наименьших заданных высот
 * таблице над ней осталась бы одна шапка — место добавляет сама диаграмма: заданная ниже высота
 * показывается не ниже той, в которую входят заголовок, наименьшая таблица и легенда.
 *
 * Высоту содержимого ячейке передаёт её прямой потомок, и только пока его наименьшая высота не
 * обнулена и он не прокручиваемый: обрезка `clip` держит содержимое в границах так же, как
 * `hidden`, но наименьшую высоту не гасит.
 */
const CHART_CELL_KEEPS_TABLE_CLASS =
  'has-[[data-keeps-table]]:min-h-min has-[[data-keeps-table]]:*:min-h-auto has-[[data-keeps-table]]:*:overflow-clip';

/**
 * Классы места диаграммы на сетке: место тянется по своим дорожкам, а ячейка диаграммы в нём — по
 * {@link CHART_CELL_CLASS}. На узком экране место встаёт по порядку разметки и высотой с диаграмму.
 */
export const CHART_PLACE_CLASS = cn('flex min-w-0 flex-col', CHART_GRID_AREA_CLASS);

/**
 * Классы ячейки диаграммы в её месте: отступ, дающий зазор с соседками. Ячейка — сетка из одного
 * элемента: карточка диаграммы растягивается по ней.
 *
 * Ячейка дотягивается до границ своего места — диаграмма колонки до высоты места, нижняя — до низа
 * строки, одиночная — на всю строку, в том числе с заданной высотой: заданная высота
 * ({@link chartCellStyle}) — наименьшая высота диаграммы в строке, и рядом с более высокой соседкой
 * диаграмма дотягивается до неё.
 */
export const CHART_CELL_CLASS = cn('grid min-w-0 grow', CHART_CELL_GUTTER_CLASS, CHART_CELL_KEEPS_TABLE_CLASS);

/**
 * Классы карточки внутри ячейки: карточка занимает отведённые диаграмме ширину и высоту и за
 * них не выходит.
 *
 * Прокрутки на карточке нет: прокручивается содержимое диаграммы внутри неё, поэтому полоса
 * появляется только по той стороне, где содержимому не хватило места, и не идёт поверх заголовка.
 */
export const CHART_CARD_CLASS = 'grid min-h-0 min-w-0 overflow-hidden';

/**
 * Высота ячейки диаграммы — наименьшая: по своему месту ячейка тянется и выше ({@link
 * CHART_CELL_CLASS}). Без заданной высоты ячейка тянется по содержимому.
 */
export const chartCellStyle = (height?: number | null): CSSProperties | undefined => (height ? { height } : undefined);

/**
 * Высота таблицы внутри диаграммы: у диаграммы с заданной высотой таблица занимает остаток
 * карточки над легендой и прокручивает строки сама, иначе тянется по своим строкам в пределах
 * потолка блоков дашборда.
 */
export const chartTableHeight = (rowCount: number, hasFixedHeight?: boolean): number | string =>
  hasFixedHeight ? '100%' : dashboardGridHeight(rowCount);

/**
 * Наименьшая высота таблицы внутри диаграммы — её шапка, итог и хотя бы одна строка выборки.
 *
 * Заданная высота бывает и меньше: у наименьших высот на шапку с итогом уходит всё место, и
 * строкам не остаётся ни одной точки. Таблица тогда встаёт этой наименьшей высотой, а не
 * поместившееся прокручивается в отведённом таблице месте — выборка остаётся достижимой при любой
 * заданной высоте.
 */
const CHART_TABLE_MIN_HEIGHT = dashboardGridHeight(1);

/**
 * Стиль таблицы внутри диаграммы и места таблицы с легендой под ним: заданная высота не отнимает у
 * строк место под них. Место с легендой не уже наименьшей таблицы, иначе прокрутка этого места
 * показывала бы шапку и итог порознь, а строку — ни разу целиком.
 */
export const chartTableStyle = (hasFixedHeight?: boolean): CSSProperties | undefined =>
  hasFixedHeight ? { minHeight: CHART_TABLE_MIN_HEIGHT } : undefined;
